import { useState } from "react";
import { motion } from "framer-motion";
import {
  Gift, Zap, TrendingUp, Crown, ArrowRight, RefreshCw, Check,
  Bot, Type, Search, FileDown, Sparkles, Monitor,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../ui/button";
import { useAuth } from "../../../lib/auth-context";
import { useSubscription } from "../../../lib/subscription-context";
import {
  PLAN_LIST,
  AI_FEATURE_ROWS,
  SUBSCRIPTION_PLANS,
  formatDevices,
} from "../../../lib/subscription-plans";
import { getErrorMessage } from "../../../lib/api";

// ─── Constants ────────────────────────────────────────────────────────────────

const PLAN_ICONS = { Gift, Zap, TrendingUp, Crown };

const STATUS_BADGE = {
  active:    "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30",
  trial:     "bg-blue-500/20 text-blue-400 border border-blue-500/30",
  expired:   "bg-red-500/20 text-red-400 border border-red-500/30",
  cancelled: "bg-slate-500/20 text-slate-400 border border-slate-600/40",
};

const FEATURE_META = {
  aiListing:      { icon: Bot,      bg: "bg-violet-500/20", text: "text-violet-400" },
  titleOptimise:  { icon: Type,     bg: "bg-blue-500/20",   text: "text-blue-400"   },
  descriptionSeo: { icon: Search,   bg: "bg-teal-500/20",   text: "text-teal-400"   },
  xlsxExport:     { icon: FileDown, bg: "bg-amber-500/20",  text: "text-amber-400"  },
  enhancementAi:  { icon: Sparkles, bg: "bg-pink-500/20",   text: "text-pink-400"   },
};

// ─── Usage card ───────────────────────────────────────────────────────────────

const UsageCard = ({ featureKey, label, used, limit }) => {
  const isUnlimited = limit === Infinity;
  const isLocked    = limit === 0;
  const left        = isUnlimited ? "∞" : isLocked ? "—" : Math.max(0, limit - used);
  const badge       = isUnlimited ? "∞" : isLocked ? "—" : `${used}/${limit}`;
  const pct         = isLocked || isUnlimited ? 0 : Math.min(100, (used / limit) * 100);
  const badgeColor  = pct >= 100 ? "text-red-400" : pct >= 80 ? "text-amber-400" : "text-emerald-400";
  const meta        = FEATURE_META[featureKey] ?? { icon: Sparkles, bg: "bg-purple-500/20", text: "text-purple-400" };
  const Icon        = meta.icon;

  return (
    <div className="rounded-2xl bg-slate-800/60 border border-slate-700/60 p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${meta.bg} ${meta.text}`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className={`text-xs font-bold tabular-nums ${badgeColor}`}>{badge}</span>
      </div>

      <div className="font-semibold text-sm text-white leading-tight">{label}</div>

      <div className="mt-auto">
        {!isLocked && !isUnlimited && (
          <div className="h-[3px] bg-slate-700 rounded-full overflow-hidden mb-2.5">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                pct >= 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500"
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
        {isUnlimited && (
          <div className="h-[3px] bg-emerald-500/40 rounded-full mb-2.5" />
        )}
        <div className="flex justify-between text-[11px] font-medium text-slate-500">
          <span>{isLocked ? "Locked" : `${used} used`}</span>
          <span>
            {isUnlimited ? "Unlimited" : isLocked ? "" : `${left} left`}
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

export const SubscriptionPage = () => {
  const { user } = useAuth();
  const { subscription, usage, trialUsed, loading, subscribe, refreshSubscription } = useSubscription();
  const [subscribingId, setSubscribingId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const currentPlan = subscription ? SUBSCRIPTION_PLANS[subscription.plan] : null;

  const expiryLabel = subscription?.expiresAt
    ? new Date(subscription.expiresAt).toLocaleDateString("en-IN", {
        day: "numeric", month: "short", year: "numeric",
      })
    : "—";

  const handleSubscribe = async (plan) => {
    setSubscribingId(plan.id);
    try {
      await subscribe(plan.id, { name: user?.name, email: user?.email });
      toast.success(`${plan.name} plan activated!`, {
        description: plan.isFree
          ? "Your free 3-day trial has started."
          : "Your subscription is now active. Enjoy your daily limits.",
      });
    } catch (err) {
      if (err?.message !== "Payment cancelled") {
        toast.error("Subscription failed", { description: getErrorMessage(err) });
      }
    } finally {
      setSubscribingId(null);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try { await refreshSubscription(); } catch { /* best-effort */ }
    finally { setRefreshing(false); }
  };

  return (
    <div className="space-y-6" data-testid="panel-subscription">

      {/* ── Unified dark card: plan info + usage ─────────────────────────── */}
      <div className="rounded-3xl bg-slate-900 border border-slate-800 overflow-hidden">

        {/* Plan info row */}
        <div className="p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-6">

          {/* Left: plan details */}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-3">
              Current Plan
            </div>

            {loading ? (
              <div className="font-heading text-4xl font-extrabold text-white/20 animate-pulse">
                Loading…
              </div>
            ) : currentPlan ? (
              <>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
                    {currentPlan.name}
                  </span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${
                    STATUS_BADGE[subscription.status] ?? "bg-slate-700 text-slate-300"
                  }`}>
                    {subscription.status}
                  </span>
                </div>
                <p className="mt-1.5 text-sm text-slate-400">
                  {currentPlan.isFree
                    ? `Free trial · Expires ${expiryLabel}`
                    : `₹${currentPlan.price}/month · Renews ${expiryLabel}`}
                </p>
              </>
            ) : (
              <>
                <div className="font-heading text-3xl font-extrabold text-white/40">
                  No active plan
                </div>
                <p className="mt-1.5 text-sm text-slate-500">
                  Choose a plan below to unlock AI features.
                </p>
              </>
            )}
          </div>

          {/* Right: devices box */}
          <div className="shrink-0 rounded-2xl bg-slate-800 border border-slate-700 px-6 py-4 flex items-center gap-4 sm:flex-col sm:items-start sm:gap-1 sm:min-w-[130px]">
            <Monitor className="w-4 h-4 text-slate-500 sm:mb-1" />
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 leading-none mb-1">
                Devices
              </div>
              <div className="font-heading text-3xl font-extrabold text-white leading-none">
                {currentPlan
                  ? currentPlan.devices === Infinity ? "∞" : currentPlan.devices
                  : "—"}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {currentPlan ? currentPlan.loginElsewhereLabel : "No plan"}
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        {currentPlan && <div className="border-t border-slate-800 mx-6 sm:mx-8" />}

        {/* Usage grid */}
        {currentPlan && (
          <div className="p-6 sm:p-8 pt-5 sm:pt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-sm font-bold text-white">Today's Usage</div>
                <div className="text-[11px] text-slate-500 mt-0.5">Resets at midnight IST</div>
              </div>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full border border-slate-700 text-slate-400 hover:text-white hover:border-slate-600 text-xs font-medium transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3"
              data-testid="subscription-usage">
              {AI_FEATURE_ROWS.map(({ key, label }) => (
                <UsageCard
                  key={key}
                  featureKey={key}
                  label={label}
                  used={usage[key] ?? 0}
                  limit={currentPlan.limits[key]}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Plan picker ──────────────────────────────────────────────────── */}
      <div>
        <div className="font-heading text-xl font-extrabold text-slate-900 mb-1">
          {currentPlan ? "Change plan" : "Choose a plan"}
        </div>
        <div className="text-sm text-slate-500 mb-5">
          {currentPlan
            ? "Switch or upgrade at any time. New limits apply immediately."
            : "Start with a free 3-day trial or jump straight to a paid plan."}
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
          {PLAN_LIST.filter((plan) => !(plan.id === "trial" && trialUsed)).map((plan, i) => {
            const Icon      = PLAN_ICONS[plan.icon] || Zap;
            const isCurrent = subscription?.plan === plan.id;
            const isPopular = plan.popular;

            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
                data-testid={`subscription-plan-${plan.id}`}
                className={`relative rounded-2xl p-px ${
                  isCurrent
                    ? "bg-gradient-to-br from-purple-500 to-violet-600 shadow-lg shadow-purple-500/20"
                    : isPopular
                    ? "bg-gradient-to-br from-blue-500 to-blue-700"
                    : "bg-slate-200"
                }`}
              >
                <div className="relative h-full rounded-[14px] bg-white p-5 flex flex-col">
                  {isCurrent && (
                    <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-purple-600 text-white text-[10px] font-bold">
                      Current
                    </span>
                  )}
                  {isPopular && !isCurrent && (
                    <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-blue-500 text-white text-[10px] font-bold">
                      Popular
                    </span>
                  )}

                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      isCurrent ? "bg-purple-600 text-white" : "bg-purple-50 text-purple-700"
                    }`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="font-heading text-sm font-bold uppercase tracking-wider text-slate-700">
                      {plan.name}
                    </div>
                  </div>

                  <div className="flex items-baseline gap-1 mb-0.5">
                    <span className="font-heading text-3xl font-extrabold text-slate-900">
                      {plan.priceLabel}
                    </span>
                    <span className="text-xs text-slate-500">/ {plan.period}</span>
                  </div>
                  <div className="text-xs text-slate-500 mb-4">{plan.tagline}</div>

                  <ul className="space-y-1.5 mb-4 flex-1">
                    {AI_FEATURE_ROWS.slice(0, 3).map(({ key, label }) => {
                      const limit = plan.limits[key];
                      const text  = limit === Infinity ? "Unlimited" : limit === 0 ? "Locked" : `${limit}/day`;
                      return (
                        <li key={key} className="flex items-center gap-2 text-xs text-slate-600">
                          <Check
                            className={`w-3.5 h-3.5 shrink-0 ${limit === 0 ? "text-slate-300" : "text-purple-500"}`}
                            strokeWidth={2.5}
                          />
                          {label}:{" "}
                          <span className={`font-semibold ${limit === 0 ? "text-slate-400" : "text-slate-800"}`}>
                            {text}
                          </span>
                        </li>
                      );
                    })}
                    <li className="text-xs text-slate-400 pl-5">
                      {formatDevices(plan.devices).label} · {plan.loginElsewhereLabel}
                    </li>
                  </ul>

                  <Button
                    onClick={() => !isCurrent && handleSubscribe(plan)}
                    disabled={isCurrent || subscribingId === plan.id}
                    data-testid={`subscription-subscribe-${plan.id}`}
                    className={`w-full rounded-full h-10 font-semibold text-sm ${
                      isCurrent
                        ? "bg-purple-50 text-purple-700 cursor-default hover:bg-purple-50"
                        : isPopular
                        ? "bg-blue-500 hover:bg-blue-600 text-white"
                        : "bg-slate-900 hover:bg-slate-800 text-white"
                    }`}
                  >
                    {isCurrent ? "Current Plan"
                      : subscribingId === plan.id ? "Processing…"
                      : <>{plan.cta} <ArrowRight className="w-3.5 h-3.5 ml-1" /></>}
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
